import {
  Button,
  CleanUpIcon,
  makeStyles,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  useTranslation
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { getDetails, getQuoteOptions } from '@paintscout/util/builder'
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import TuneIcon from '@material-ui/icons/TuneOutlined'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import HiddenItemsIcon from '@material-ui/icons/IndeterminateCheckBoxOutlined'
import classnames from 'classnames'
import React, { useState } from 'react'
import { useQuote } from '../context'
import WorkOrderNavMenu from './WorkOrderNavMenu'
import type { WorkOrderNavMenuItem } from './WorkOrderNavMenu'

/** Which card sections the view renders. 'all' shows both. */
export type WorkOrderSectionFilter = 'all' | 'area' | 'substrate'

export interface WorkOrderNavSection {
  key: WorkOrderSectionFilter
  label: string
  Icon?: React.ComponentType<{ className?: string }>
}

export interface WorkOrderNavBarNextProps {
  /** The filter pills to offer, in order (the view builds these from what has content). */
  sections: WorkOrderNavSection[]
  /** Currently-selected filter. */
  value: WorkOrderSectionFilter
  onChange: (value: WorkOrderSectionFilter) => void
  hiddenCount: number
  /** Whether the Hidden items section is expanded (the view renders it above the cards). */
  hiddenOpen: boolean
  onToggleHidden: () => void
  /**
   * Clean-up handlers keyed by section key ('area' | 'substrate'); a key is present only when that
   * view has customizations. The bar shows a single Clean up for whatever is currently showing.
   */
  cleanUp?: Partial<Record<string, () => void>>
  /** Global hour-visibility handler (mirrors WorkOrderView.handleToggleAmount). */
  onToggleAmount: (dropdownValue: string) => void
  /** Toggles the product lines on every card (quote option `showWorkOrderProducts`). */
  onToggleProducts: () => void
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1),
        '@media print': { display: 'none' },
        // The prototype's phone nav row (ENG-2080): 48px, 7px above and below.
        [theme.breakpoints.down('sm')]: {
          gap: theme.spacing(1.5),
          minHeight: 48,
          padding: theme.spacing(0.875, 0),
          marginBottom: 0,
          boxSizing: 'border-box'
        }
      },
      pills: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        minWidth: 0
      },
      pill: {
        textTransform: 'none',
        fontWeight: theme.typography.fontWeightRegular as number,
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P50,
        border: `0.8px solid ${neutral.P85}`,
        // Fully round, matching the mock's 26px-radius filter pills.
        borderRadius: theme.borderRadius?.full ?? '9999px',
        backgroundColor: neutral.White,
        minHeight: 28,
        padding: theme.spacing(0, 1.5),
        '& .MuiButton-startIcon': { marginRight: theme.spacing(0.5) },
        '& svg': { fontSize: 16 },
        '&:hover': { backgroundColor: neutral.P98, borderColor: neutral.P85 }
      },
      // A pill with an icon (Areas / Surfaces) sits tighter on the icon side and sets its label at 13px.
      pillWithIcon: {
        paddingLeft: theme.spacing(1),
        fontSize: theme.typography.pxToRem(13)
      },
      // Selected filter: filled, darker label, same outline.
      pillActive: {
        backgroundColor: neutral.P96,
        color: neutral.P30,
        '&:hover': { backgroundColor: '#e8ecf2', borderColor: neutral.P85 }
      },
      // The narrow phone's single filter dropdown: always filled, like a selected pill, with a solid caret.
      dropdownPill: {
        backgroundColor: neutral.P96,
        color: neutral.P30,
        '&:hover': { backgroundColor: '#e8ecf2', borderColor: neutral.P85 }
      },
      right: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexShrink: 0
      },
      // Compact action chips (the prototype's 28px / 12px-label row): quiet until hovered, with a
      // pressed fill while their state is applied.
      actionButton: {
        textTransform: 'none',
        color: neutral.P50,
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(13),
        minHeight: 28,
        padding: theme.spacing(0, 1),
        // `lg` (8px) — closest token to the mock's 6px action chips. (`sm` is 2px, not 6.)
        borderRadius: theme.borderRadius?.lg ?? 8,
        '& .MuiButton-startIcon': { marginRight: theme.spacing(0.5) },
        '& svg': { fontSize: 16 },
        '&:hover': { backgroundColor: neutral.P96 }
      },
      // Pressed state for a toggle action that is currently applied (products hidden, section open).
      actionButtonActive: {
        backgroundColor: neutral.P96,
        color: neutral.P40,
        '&:hover': { backgroundColor: neutral.P94 }
      },
      actionDivider: {
        width: 1,
        height: 11,
        borderRadius: theme.borderRadius.full,
        backgroundColor: neutral.P85
      },
      pillLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5)
      },
      // Solid carets, as the prototype draws them, turning over while their menu is open.
      caret: {
        fontSize: 20,
        margin: -3,
        color: neutral.P50,
        transition: 'transform 0.15s'
      },
      caretOpen: {
        transform: 'rotate(180deg)'
      },
      // The phone "View" trigger: a quiet 28px chip that fills while its menu is open.
      viewButton: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        height: 28,
        padding: theme.spacing(0, 1),
        border: 'none',
        borderRadius: 6,
        background: 'transparent',
        font: 'inherit',
        cursor: 'pointer',
        transition: 'background-color 0.15s',
        '&:hover': { backgroundColor: '#f0f2f5' }
      },
      viewButtonOpen: {
        backgroundColor: '#f0f2f5'
      },
      viewLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        fontSize: theme.typography.pxToRem(14),
        lineHeight: 1.4,
        color: neutral.P50,
        whiteSpace: 'nowrap'
      },
      viewIcon: {
        fontSize: 16,
        color: neutral.P50
      },
      viewIconLarge: {
        fontSize: 20
      }
    }
  },
  { name: 'WorkOrderNavBarNext' }
)

/** Hour-visibility dropdown options (mirrors the old per-section control). */
function getDefaultToggleOption(quoteOptions: ReturnType<typeof getQuoteOptions>): string {
  const itemKeys = [
    'showWorkOrderAreaValues',
    'showWorkOrderSubstrateValues',
    'showWorkOrderAreaOptionValues',
    'showWorkOrderSubstrateOptionValues'
  ]
  const allTrue = itemKeys.every((key) => quoteOptions[key] === true)
  const allFalse = itemKeys.every((key) => quoteOptions[key] === false)
  if (allTrue) {
    return quoteOptions.showWorkOrderTotalHours === false ? 'hideTotalHours' : 'showAllHours'
  }
  if (allFalse) {
    return quoteOptions.showWorkOrderTotalHours === true ? 'hideItemHours' : 'hideAllHours'
  }
  return 'showAllHours'
}

/**
 * The Work Order top navigation bar. Left: All / Areas / Surfaces pills that filter which card
 * sections the view renders (collapsing to a single dropdown on mobile). Right: "Hidden items (N)"
 * (expands the section the view renders above the cards), Clean up for whatever is showing, a "Hide
 * all products" toggle, and the global hours dropdown.
 */
export default function WorkOrderNavBarNext({
  sections,
  value,
  onChange,
  hiddenCount,
  hiddenOpen,
  onToggleHidden,
  cleanUp,
  onToggleAmount,
  onToggleProducts
}: WorkOrderNavBarNextProps) {
  const classes = useStyles()
  const theme = useTheme()
  // Collapse the left pills into a dropdown once the row gets tight (keeps the right-side actions —
  // Clean up / Hidden items / Show all hours — together on one line).
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  // Below 406px the filter pills don't fit beside View, so they fold into one dropdown (the prototype's
  // breakpoint, ENG-2080).
  const isNarrow = useMediaQuery('@media (max-width: 405px)')
  const { __ } = useTranslation()
  const { quote } = useQuote()
  const { options } = useClientOptions()

  const [navAnchor, setNavAnchor] = useState<HTMLElement | null>(null)
  const [hoursAnchor, setHoursAnchor] = useState<HTMLElement | null>(null)
  // Mobile: every right-side action collapses behind one "View" chip (see viewMenu).
  const [viewAnchor, setViewAnchor] = useState<HTMLElement | null>(null)

  const quoteOptions = getQuoteOptions({ options, quote })
  const productsHidden = quoteOptions.showWorkOrderProducts === false
  const details = getDetails({ quote, options })
  const allowShowHours = !details.totals.hours.useCustom || quoteOptions.allowShowCustomizedTotalHours
  const selectedToggle = getDefaultToggleOption(quoteOptions)
  const hourOptions = [
    { value: 'showAllHours', label: __('Show all hours'), icon: VisibilityIcon },
    { value: 'hideItemHours', label: __('Hide item hours'), icon: VisibilityOffIcon },
    { value: 'hideAllHours', label: __('Hide all hours'), icon: VisibilityOffIcon }
  ]
  const selectedHourOption = hourOptions.find((option) => option.value === selectedToggle) ?? hourOptions[0]

  const hoursDropdown = (
    <>
      <span className={classes.actionDivider} />
      {allowShowHours ? (
        <Button
          variant="text"
          size="small"
          className={classes.actionButton}
          startIcon={<VisibilityIcon />}
          endIcon={hoursAnchor ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={(ev: React.MouseEvent<HTMLElement>) => setHoursAnchor(ev.currentTarget)}
        >
          {selectedHourOption.label}
        </Button>
      ) : (
        <Tooltip
          content={__('Hours cannot be shown because Total Hours have been customized')}
          placement="bottom"
          hideIcon
        >
          <span>
            <Button
              variant="text"
              size="small"
              className={classes.actionButton}
              startIcon={<VisibilityIcon />}
              endIcon={<ExpandMoreIcon />}
              disabled
            >
              {selectedHourOption.label}
            </Button>
          </span>
        </Tooltip>
      )}
      {/* The same menu as the phone View chip (ENG-2080); desktop keeps its pick-one-of-three. */}
      <WorkOrderNavMenu
        anchorEl={hoursAnchor}
        onClose={() => setHoursAnchor(null)}
        align="right"
        minWidth={220}
        items={hourOptions.map((option) => ({
          key: option.value,
          label: option.label,
          icon: option.icon,
          selected: option.value === selectedToggle,
          onSelect: () => onToggleAmount(option.value)
        }))}
      />
    </>
  )

  const activeSection = sections.find((section) => section.key === value) ?? sections[0]
  // A single Clean up for what's on screen: the selected view, or — while showing everything —
  // whichever view has something to clean up (area first).
  const activeCleanUp = value === 'all' ? cleanUp?.area ?? cleanUp?.substrate : cleanUp?.[value]

  // Phones: one "View" chip in place of the Hidden items / Clean up / products / hours chips — four
  // targets don't fit beside the section filter. Its menu is the prototype's (ENG-2080): each hours item
  // flips between Hide and Show, rather than the desktop's pick-one-of-three. Clean up has no place in
  // the prototype, but it is the only way to reset a rearranged view, so it closes the list.
  const itemHoursHidden = selectedToggle === 'hideItemHours'
  const allHoursHidden = selectedToggle === 'hideAllHours'
  const hoursDisabledReason = allowShowHours
    ? undefined
    : __('Hours cannot be shown because Total Hours have been customized')
  const viewItems: WorkOrderNavMenuItem[] = [
    {
      key: 'products',
      label: productsHidden ? __('Show all products') : __('Hide all products'),
      icon: productsHidden ? VisibilityIcon : VisibilityOffIcon,
      onSelect: onToggleProducts
    },
    {
      key: 'item-hours',
      label: itemHoursHidden ? __('Show item hours') : __('Hide item hours'),
      icon: itemHoursHidden ? VisibilityIcon : VisibilityOffIcon,
      disabled: !allowShowHours,
      title: hoursDisabledReason,
      onSelect: () => onToggleAmount(itemHoursHidden ? 'showAllHours' : 'hideItemHours')
    },
    {
      key: 'all-hours',
      label: allHoursHidden ? __('Show all hours') : __('Hide all hours'),
      icon: allHoursHidden ? VisibilityIcon : VisibilityOffIcon,
      disabled: !allowShowHours,
      title: hoursDisabledReason,
      divider: true,
      onSelect: () => onToggleAmount(allHoursHidden ? 'showAllHours' : 'hideAllHours')
    },
    ...(hiddenCount > 0
      ? [
          {
            key: 'hidden',
            label: `${__('Hidden items')} (${hiddenCount})`,
            icon: HiddenItemsIcon,
            selected: hiddenOpen,
            onSelect: onToggleHidden
          }
        ]
      : []),
    ...(activeCleanUp ? [{ key: 'clean-up', label: __('Clean up'), icon: CleanUpIcon, onSelect: activeCleanUp }] : [])
  ]

  const viewMenu = (
    <>
      <button
        type="button"
        aria-haspopup="true"
        className={classnames(classes.viewButton, { [classes.viewButtonOpen]: !!viewAnchor })}
        onClick={(ev) => setViewAnchor(ev.currentTarget)}
      >
        <span className={classes.viewLabel}>
          {/* The narrow form swaps the eye for the smaller sliders glyph, as the prototype does. */}
          {isNarrow ? (
            <TuneIcon className={classes.viewIcon} />
          ) : (
            <VisibilityOffIcon className={classnames(classes.viewIcon, classes.viewIconLarge)} />
          )}
          {__('View')}
        </span>
        <ArrowDropDownIcon className={classnames(classes.caret, { [classes.caretOpen]: !!viewAnchor })} />
      </button>
      <WorkOrderNavMenu
        anchorEl={viewAnchor}
        onClose={() => setViewAnchor(null)}
        items={viewItems}
        align="right"
        minWidth={220}
      />
    </>
  )

  const filterPills = (
    <div className={classes.pills}>
      {sections.map((section) => (
        <Button
          key={section.key}
          variant="text"
          size="small"
          icon={section.Icon}
          className={classnames(classes.pill, {
            [classes.pillWithIcon]: !!section.Icon,
            [classes.pillActive]: section.key === value
          })}
          onClick={() => onChange(section.key)}
        >
          {section.label}
        </Button>
      ))}
    </div>
  )

  return (
    <div className={classes.root}>
      {/* Left: which sections to show */}
      {isMobile && isNarrow ? (
        <div className={classes.pills}>
          <Button
            variant="text"
            size="small"
            className={classnames(classes.pill, classes.dropdownPill)}
            icon={activeSection?.Icon}
            onClick={(ev: React.MouseEvent<HTMLElement>) => setNavAnchor(ev.currentTarget)}
          >
            {/* Its own flex row: Button wraps children in a plain inline span, which sat the caret on the
                text baseline instead of centring it. */}
            <span className={classes.pillLabel}>
              {activeSection?.label}
              <ArrowDropDownIcon className={classnames(classes.caret, { [classes.caretOpen]: !!navAnchor })} />
            </span>
          </Button>
          <WorkOrderNavMenu
            anchorEl={navAnchor}
            onClose={() => setNavAnchor(null)}
            align="left"
            minWidth={180}
            items={sections.map((section) => ({
              key: section.key,
              label: section.label,
              icon: section.Icon,
              selected: section.key === value,
              onSelect: () => onChange(section.key)
            }))}
          />
        </div>
      ) : (
        filterPills
      )}

      {/* Right: Hidden items + Clean up + Hide all products + global hours — always one line on
          desktop, all of it behind the "View" chip on mobile. */}
      <div className={classes.right}>
        {isMobile && viewMenu}
        {!isMobile && hiddenCount > 0 && (
          <Button
            variant="text"
            size="small"
            icon={VisibilityOffIcon}
            className={classnames(classes.actionButton, { [classes.actionButtonActive]: hiddenOpen })}
            onClick={onToggleHidden}
          >
            <Typography component="span" variant="inherit">{`${__('Hidden items')} (${hiddenCount})`}</Typography>
          </Button>
        )}
        {!isMobile && activeCleanUp && (
          <Button
            variant="text"
            size="small"
            icon={CleanUpIcon}
            className={classes.actionButton}
            onClick={activeCleanUp}
          >
            {__('Clean up')}
          </Button>
        )}
        {/* Product lines on every card. The label names what the click does; the button also takes a
            pressed look while products are hidden (the mock's active-chip state). The divider only
            earns its place when something sits to its left. */}
        {!isMobile && (hiddenCount > 0 || activeCleanUp) && <span className={classes.actionDivider} />}
        {!isMobile && (
          <Button
            variant="text"
            size="small"
            icon={productsHidden ? VisibilityIcon : VisibilityOffIcon}
            className={classnames(classes.actionButton, { [classes.actionButtonActive]: productsHidden })}
            onClick={onToggleProducts}
          >
            {productsHidden ? __('Show all products') : __('Hide all products')}
          </Button>
        )}
        {!isMobile && hoursDropdown}
      </div>
    </div>
  )
}
